import { useState, useEffect } from "react";
import { DashboardLayout, teamLeaderNavItems } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { 
  Search, 
  User, 
  Star, 
  Briefcase, 
  MapPin, 
  Clock, 
  CheckCircle2,
  Award,
  MessageSquare,
  UserPlus,
  Filter,
  ChevronRight,
  Phone,
  Mail
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FindWorkers() {
  const { t } = useTranslation();
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [skillFilter, setSkillFilter] = useState("");
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [workerReviews, setWorkerReviews] = useState([]);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [canRateWorker, setCanRateWorker] = useState(false);
  const [ratingDialogOpen, setRatingDialogOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [ratingComment, setRatingComment] = useState("");
  const [submittingRating, setSubmittingRating] = useState(false);
  const [allSkills, setAllSkills] = useState([]);

  useEffect(() => {
    fetchWorkers();
  }, []);

  const fetchWorkers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/workers/available-for-team`);
      setWorkers(res.data.workers || []);
      setAllSkills(res.data.all_skills || []);
    } catch (err) {
      console.error("Failed to fetch workers:", err);
      toast.error("Failed to load workers");
    } finally {
      setLoading(false);
    }
  };

  const openWorkerDetails = async (worker) => {
    setSelectedWorker(worker);
    setDetailsOpen(true);
    
    // Fetch reviews
    try {
      const res = await axios.get(`${API}/worker-ratings/${worker.user_id}`);
      setWorkerReviews(res.data.ratings || []);
    } catch (err) {
      setWorkerReviews([]);
    }
    
    // Check if TL can rate this worker
    try {
      const res = await axios.get(`${API}/workers/${worker.user_id}/can-rate`);
      setCanRateWorker(res.data.can_rate);
    } catch (err) {
      setCanRateWorker(false);
    }
  };

  const submitRating = async () => {
    if (rating === 0) {
      toast.error("Please select a rating");
      return;
    }
    
    setSubmittingRating(true);
    try {
      await axios.post(`${API}/worker-ratings`, {
        worker_id: selectedWorker.user_id,
        rating: rating,
        comment: ratingComment.trim() || null
      });
      toast.success("Review submitted!");
      setRatingDialogOpen(false);
      setRating(0);
      setRatingComment("");
      // Refresh worker data
      fetchWorkers();
      openWorkerDetails(selectedWorker);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmittingRating(false);
    }
  };

  const addToFriends = async (worker) => {
    try {
      await axios.post(`${API}/chat/friends/add`, {
        user_id: worker.user_id
      });
      toast.success(`Added ${worker.name} to contacts!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add contact");
    }
  };

  // Filter workers
  const filteredWorkers = workers.filter(w => {
    const matchesSearch = 
      w.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      w.worker_code?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesSkill = !skillFilter || 
      w.skills?.some(s => s.toLowerCase().includes(skillFilter.toLowerCase()));
    
    return matchesSearch && matchesSkill;
  });

  // Render star rating
  const renderStars = (rating, size = "sm") => {
    const starSize = size === "sm" ? "w-4 h-4" : "w-5 h-5";
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${starSize} ${
              star <= Math.round(rating)
                ? "fill-yellow-400 text-yellow-400"
                : "text-gray-600"
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <DashboardLayout navItems={teamLeaderNavItems} title="Find Workers">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white font-['Unbounded']">
              Find New Team Members
            </h1>
            <p className="text-[#94A3B8] mt-1">
              Browse available workers and add them to your team
            </p>
          </div>
          <Badge variant="outline" className="border-[#23F7DD]/50 text-[#23F7DD]">
            {filteredWorkers.length} workers available
          </Badge>
        </div>

        {/* Search and Filter */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name or code (WRK-xxx)..."
                  className="bg-black/50 border-white/10 text-white pl-10"
                />
              </div>
              <div className="relative sm:w-64">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                <select
                  value={skillFilter}
                  onChange={(e) => setSkillFilter(e.target.value)}
                  className="w-full h-10 pl-10 pr-4 bg-black/50 border border-white/10 text-white rounded-md appearance-none cursor-pointer"
                >
                  <option value="">All Skills</option>
                  {allSkills.map((skill) => (
                    <option key={skill} value={skill}>{skill}</option>
                  ))}
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Workers Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredWorkers.length === 0 ? (
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-12 text-center">
              <User className="w-16 h-16 mx-auto text-[#475569] mb-4" />
              <p className="text-[#94A3B8]">No workers found matching your criteria</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredWorkers.map((worker) => (
              <Card 
                key={worker.user_id} 
                className="bg-[#0A0A0A] border-white/10 hover:border-[#23F7DD]/50 transition-all cursor-pointer group"
                onClick={() => openWorkerDetails(worker)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Avatar */}
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#23F7DD]/30 to-purple-500/30 flex items-center justify-center flex-shrink-0">
                      {worker.profile_photo ? (
                        <img 
                          src={`${process.env.REACT_APP_BACKEND_URL}${worker.profile_photo}`}
                          alt={worker.name}
                          className="w-14 h-14 rounded-full object-cover"
                        />
                      ) : (
                        <User className="w-7 h-7 text-[#23F7DD]" />
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className="text-white font-semibold truncate">{worker.name}</h3>
                        <ChevronRight className="w-4 h-4 text-[#94A3B8] group-hover:text-[#23F7DD] transition-colors" />
                      </div>
                      <p className="text-sm text-[#23F7DD]">{worker.worker_code}</p>
                      
                      {/* Rating */}
                      <div className="flex items-center gap-2 mt-2">
                        {renderStars(worker.average_rating || 0)}
                        <span className="text-sm text-[#94A3B8]">
                          {worker.average_rating > 0 
                            ? `${worker.average_rating.toFixed(1)} (${worker.total_ratings})`
                            : "No ratings"
                          }
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Skills */}
                  {worker.skills?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {worker.skills.slice(0, 3).map((skill) => (
                        <Badge 
                          key={skill} 
                          variant="secondary" 
                          className="bg-white/5 text-[#94A3B8] text-xs"
                        >
                          {skill}
                        </Badge>
                      ))}
                      {worker.skills.length > 3 && (
                        <Badge variant="secondary" className="bg-white/5 text-[#94A3B8] text-xs">
                          +{worker.skills.length - 3}
                        </Badge>
                      )}
                    </div>
                  )}

                  {/* Availability & Experience */}
                  <div className="mt-3 flex items-center gap-4 text-xs text-[#94A3B8]">
                    <span className={`flex items-center gap-1 ${worker.is_available ? "text-green-400" : "text-yellow-400"}`}>
                      <Clock className="w-3 h-3" />
                      {worker.is_available ? "Available" : "Busy"}
                    </span>
                    {worker.completed_projects > 0 && (
                      <span className="flex items-center gap-1">
                        <Briefcase className="w-3 h-3" />
                        {worker.completed_projects} projects
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Worker Details Dialog */}
        <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Unbounded'] sr-only">Worker Details</DialogTitle>
            </DialogHeader>
            
            {selectedWorker && (
              <div className="space-y-6">
                {/* Profile Header */}
                <div className="flex items-start gap-4">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#23F7DD]/30 to-purple-500/30 flex items-center justify-center flex-shrink-0">
                    {selectedWorker.profile_photo ? (
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}${selectedWorker.profile_photo}`}
                        alt={selectedWorker.name}
                        className="w-20 h-20 rounded-full object-cover"
                      />
                    ) : (
                      <User className="w-10 h-10 text-[#23F7DD]" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-white">{selectedWorker.name}</h2>
                    <p className="text-[#23F7DD] font-medium">{selectedWorker.worker_code}</p>
                    
                    {/* Rating */}
                    <div className="flex items-center gap-3 mt-2">
                      {renderStars(selectedWorker.average_rating || 0, "md")}
                      <span className="text-[#94A3B8]">
                        {selectedWorker.average_rating > 0 
                          ? `${selectedWorker.average_rating.toFixed(1)} (${selectedWorker.total_ratings} reviews)`
                          : "No reviews yet"
                        }
                      </span>
                    </div>
                    
                    {/* Status */}
                    <div className="flex items-center gap-3 mt-2">
                      <span className={`flex items-center gap-1 text-sm ${
                        selectedWorker.is_available ? "text-green-400" : "text-yellow-400"
                      }`}>
                        {selectedWorker.is_available ? (
                          <><CheckCircle2 className="w-4 h-4" /> Available for work</>
                        ) : (
                          <><Clock className="w-4 h-4" /> Currently busy</>
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Button
                    onClick={() => addToFriends(selectedWorker)}
                    className="flex-1 bg-pink-500 text-white hover:bg-pink-600"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add to Contacts
                  </Button>
                  {canRateWorker && (
                    <Button
                      onClick={() => setRatingDialogOpen(true)}
                      className="flex-1 bg-yellow-500 text-black hover:bg-yellow-400"
                    >
                      <Star className="w-4 h-4 mr-2" />
                      Write Review
                    </Button>
                  )}
                </div>

                {/* Skills */}
                {selectedWorker.skills?.length > 0 && (
                  <div>
                    <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                      <Award className="w-5 h-5 text-[#23F7DD]" />
                      Skills
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedWorker.skills.map((skill) => (
                        <Badge 
                          key={skill} 
                          className="bg-[#23F7DD]/10 text-[#23F7DD] border-[#23F7DD]/30"
                        >
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Certifications */}
                {selectedWorker.certifications?.length > 0 && (
                  <div>
                    <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                      <Award className="w-5 h-5 text-purple-400" />
                      Certifications
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedWorker.certifications.map((cert) => (
                        <Badge 
                          key={cert} 
                          className="bg-purple-500/10 text-purple-400 border-purple-500/30"
                        >
                          {cert}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-3 bg-white/5 rounded-lg text-center">
                    <p className="text-2xl font-bold text-white">{selectedWorker.completed_projects || 0}</p>
                    <p className="text-xs text-[#94A3B8]">Projects</p>
                  </div>
                  <div className="p-3 bg-white/5 rounded-lg text-center">
                    <p className="text-2xl font-bold text-white">{selectedWorker.total_ratings || 0}</p>
                    <p className="text-xs text-[#94A3B8]">Reviews</p>
                  </div>
                  <div className="p-3 bg-white/5 rounded-lg text-center">
                    <p className="text-2xl font-bold text-[#23F7DD]">
                      {selectedWorker.average_rating > 0 
                        ? `${Math.round((selectedWorker.average_rating / 5) * 100)}%`
                        : "N/A"
                      }
                    </p>
                    <p className="text-xs text-[#94A3B8]">Rating</p>
                  </div>
                </div>

                {/* Reviews */}
                <div>
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-yellow-400" />
                    Reviews ({workerReviews.length})
                  </h3>
                  
                  {workerReviews.length === 0 ? (
                    <div className="p-4 bg-white/5 rounded-lg text-center text-[#94A3B8]">
                      No reviews yet
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {workerReviews.map((review) => (
                        <div key={review.rating_id} className="p-3 bg-white/5 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              {renderStars(review.rating)}
                              <span className="text-sm text-white font-medium">
                                {review.rated_by_name}
                              </span>
                            </div>
                            <span className="text-xs text-[#94A3B8]">
                              {new Date(review.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          {review.comment && (
                            <p className="text-sm text-[#94A3B8]">{review.comment}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {!canRateWorker && selectedWorker.total_ratings > 0 && (
                  <p className="text-xs text-center text-[#94A3B8] italic">
                    You can write a review after completing a project together
                  </p>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Rating Dialog */}
        <Dialog open={ratingDialogOpen} onOpenChange={setRatingDialogOpen}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Unbounded'] flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-400" />
                Write Review for {selectedWorker?.name}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Star Rating */}
              <div className="flex justify-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="focus:outline-none transition-transform hover:scale-110"
                  >
                    <Star
                      className={`w-10 h-10 transition-colors ${
                        star <= (hoverRating || rating)
                          ? "fill-yellow-400 text-yellow-400"
                          : "text-gray-600 hover:text-yellow-400/50"
                      }`}
                    />
                  </button>
                ))}
              </div>
              
              <p className="text-center text-[#94A3B8]">
                {rating === 0 && "Select a rating"}
                {rating === 1 && "Poor"}
                {rating === 2 && "Below Average"}
                {rating === 3 && "Average"}
                {rating === 4 && "Good"}
                {rating === 5 && "Excellent"}
              </p>
              
              {/* Comment */}
              <div className="space-y-2">
                <label className="text-sm text-[#94A3B8]">Comment (optional)</label>
                <Textarea
                  value={ratingComment}
                  onChange={(e) => setRatingComment(e.target.value)}
                  placeholder="Share your experience working with this person..."
                  className="bg-black/50 border-white/10 text-white min-h-[80px]"
                />
              </div>
              
              {/* Submit Button */}
              <Button
                onClick={submitRating}
                disabled={submittingRating || rating === 0}
                className="w-full bg-yellow-500 text-black hover:bg-yellow-400 font-medium"
              >
                {submittingRating ? "Submitting..." : "Submit Review"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
